import '../styles/OperationsTransferOversightBlock.css';
import React from 'react';
import { useProjectCollection } from '../lib/projectData';

function formatAmount(value) {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    .toUpperCase();
}

function TransferRow({ entry }) {
  const isCredit = entry.direction === 'credit';
  const sign = isCredit ? '+' : '−';
  const amountClass = isCredit ? 'oto-amount is-credit' : 'oto-amount is-debit';

  return (
    <li className="oto-row" tabIndex={0}>
      <div className="oto-row-inner">
        <div className="oto-row-label">
          <span className="oto-date">{formatDate(entry.date)}</span>
          <span className="oto-desc">{entry.description}</span>
          <span className="oto-counter">{entry.counterparty}</span>
        </div>
        <div className="oto-row-value">
          <span className={amountClass}>
            {sign}
            {formatAmount(entry.amount)}
          </span>
          <span className="oto-status">{entry.status}</span>
        </div>
      </div>
    </li>
  );
}

function SkeletonRows() {
  return (
    <ul className="oto-skeleton" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((n) => (
        <li className="oto-skeleton-row" key={`oto-skeleton-${n}`}>
          <div className="oto-skeleton-stack">
            <span className="oto-bar oto-bar--label" />
            <span className="oto-bar oto-bar--sub" />
          </div>
          <div className="oto-skeleton-value">
            <span className="oto-bar oto-bar--value" />
            <span className="oto-bar oto-bar--status" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function OperationsTransferOversightBlock() {
  const { data, loading, error, refresh } = useProjectCollection('ledger_entries');
  const records = Array.isArray(data) ? data : [];

  const showError = !loading && Boolean(error);
  const showEmpty = !loading && !error && records.length === 0;
  const showRows = !loading && !error && records.length > 0;

  const refreshDisabled = loading;

  return (
    <section className="oto-root" aria-labelledby="oto-transfer-activity">
      <div className="oto-panel">
        <div className="oto-head">
          <div className="oto-head-mark">
            <h2 className="oto-micro" id="oto-transfer-activity">
              Transfer Activity
            </h2>
            <p className="oto-sub">
              Bank-side oversight of client transfers, newest entry first.
            </p>
          </div>
          <button
            type="button"
            className="oto-control"
            onClick={refresh}
            disabled={refreshDisabled}
          >
            <span className="oto-glyph" aria-hidden="true" />
            {loading ? 'Refreshing' : 'Refresh'}
          </button>
        </div>

        <div aria-live="polite" aria-busy={loading}>
          {loading && <SkeletonRows />}

          {showError && (
            <div className="oto-state oto-state--error" role="alert">
              <h3 className="oto-state-title">Transfer records unavailable</h3>
              <p className="oto-state-copy">
                The transfer oversight ledger could not be read from the banking service.
                The message returned was: {error && error.message ? error.message : 'unknown error'}.
              </p>
              <button type="button" className="oto-control oto-control--primary" onClick={refresh}>
                <span className="oto-glyph" aria-hidden="true" />
                Retry
              </button>
            </div>
          )}

          {showEmpty && (
            <div className="oto-state">
              <h3 className="oto-state-title">No transfer records to oversee</h3>
              <p className="oto-state-copy">
                No client transfer activity has been recorded yet. Transfer rows appear here
                once a client initiates a movement of funds.
              </p>
              <button type="button" className="oto-control" onClick={refresh}>
                <span className="oto-glyph" aria-hidden="true" />
                Refresh
              </button>
            </div>
          )}

          {showRows && (
            <ul className="oto-ledger">
              {records.map((entry) => (
                <TransferRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

export default OperationsTransferOversightBlock;